import React, { useState, useEffect, useContext, createContext } from 'react';
import { useFormContext, useForm, FormProvider } from 'react-hook-form';

export const useWizard = ({ initialPage }) => {
    const [activePage, setActivePage] = useState(initialPage);
    const [pageTotal, setPageTotal] = useState(1);
    const [formData, setFormData] = useState({});

    const updateFormData = (pageFormData) => setFormData({...formData, ...pageFormData});
    const previousPage = () => setActivePage(activePage - 1)
    const nextPage = () => setActivePage(activePage + 1)
    const goToPage = (pageIndex) => setActivePage(pageIndex)
    const isLastPage = activePage === pageTotal - 1

    return {
        formData,
        updateFormData,
        activePage,
        pageTotal,
        previousPage,
        nextPage,
        goToPage,
        isLastPage,
        setPageTotal
    }
}

export const useWizardContext = () => {
  return useContext(WizardContext);
}

export const usePageTotal = (children, setPageTotal) => {
    useEffect(() => {
        const pagesComponent = React.Children.toArray(children)[0];
        if (pagesComponent !== undefined) {
            const pageComponents = React.Children.toArray(
                pagesComponent.props.children
            )
            setPageTotal(pageComponents.length)
        }
    }, [children, setPageTotal])
}

const WizardInner = ({children, onSubmit}) => {
    const [submitted, setSubmitted] = useState(false)
    const formContext = useFormContext()
    const { handleSubmit } = formContext
    const wizardContext = useContext(WizardContext);
    const { isLastPage, nextPage, formData, updateFormData } = wizardContext

    usePageTotal(children, wizardContext.setPageTotal)

    useEffect(() => {
        if (submitted) {
            if (!isLastPage) {
                nextPage()
            } else {
                onSubmit({ formData, formContext, wizardContext })
            }
            setSubmitted(false)
        }
    }, [
        formData,
        formContext,
        isLastPage,
        nextPage,
        onSubmit,
        submitted,
        wizardContext
    ])

    return (
        <form
          style={{display:"contents"}}
          onSubmit={handleSubmit((pageData) => {
            updateFormData(pageData);
            setSubmitted(true);
          })}
        >
          {children}
        </form>
    )
}

export const WizardContext = createContext({ initialPage: 0, pageTotal: 1 })
export const WizardContextProvider = ({ children, initialPage, onSubmit }) => {
  const wizard = useWizard({ initialPage });

  return (
    <WizardContext.Provider value={wizard}>
      <WizardInner onSubmit={onSubmit}>{children}</WizardInner>
    </WizardContext.Provider>
  );
}

export const Wizard = ({children, formArgs, onSubmit}) => {
  const formContextMethods = useForm(formArgs);

  return (
    <FormProvider {...formContextMethods}>
        <WizardContextProvider
            initialPage={0}
            onSubmit={onSubmit}
        >
            {children}
        </WizardContextProvider>
    </FormProvider>
  );
}

export const Pages = ({ children }) => {
    const { activePage } = useContext(WizardContext);
    return Array.isArray(children) ? children[activePage] : children;
}

export const Page = ({ children }) => {
    const formContext = useFormContext()
    const wizardContext = useContext(WizardContext)

    if (typeof children === 'function') {
        return children({ formContext, wizardContext })
    }

    return children
}
